// We disable no-param-reassign because we need to mutate the object
/* eslint-disable no-param-reassign */
import { useCallback } from 'react';
import { EProgram, EQuestionTypes, SurveyOptions, SurveySender } from '../../types/surveys';
import { ErrorsService } from '../../services/ErrorsService';

import {
  MenopauseSymptoms,
  OnboardingAnswers,
  OnboardingStorageData,
} from '../../types/onboarding/survey';
import { getMenopauseSymptoms, handleMenopauseSymptoms } from '../../utils/onboarding/menopause';
import { calculateTrackDueDateRecordedData, getProgram } from '../../utils/onboarding/survey';
import { STORAGE_KEYS } from '../../utils/storage';
import { trackDueDateRecordedEvent } from '../../services/DataPipeline/DataPipeline';

export const useSurveyHandlers = (
  setOnboardingFinished: (finished: boolean) => void,
  setOnboardingAnswers: (answers: OnboardingAnswers) => void,
  menopauseSymptoms: MenopauseSymptoms,
  setMenopauseSymptoms: (symptoms: MenopauseSymptoms) => void,
  onSurveyComplete?: (pathInfo: EProgram, answers: OnboardingAnswers) => void,
  onError?: (error: string) => void,
  refetchStage?: ({ stage }: { stage: EProgram }) => Promise<unknown>,
  trackSurveyFinished?: (
    answers: OnboardingAnswers,
    additionalEventData: Record<string, string | number | boolean | object>,
  ) => void,
) => {
  const handleSurveyComplete = useCallback(
    async (id: string, sender: SurveySender, options: SurveyOptions) => {
      try {
        const programDetermined = getProgram(sender.data);
        // Store consolidated onboarding data
        const onboardingData: OnboardingStorageData = {
          surveyId: id,
          answers: sender.data,
          recommendedStage: programDetermined,
          pathInfo: programDetermined,
        };
        if (sender.data[EQuestionTypes.dueDate]) {
          const dueDate = sender.data[EQuestionTypes.dueDate] as string;
          trackDueDateRecordedEvent(calculateTrackDueDateRecordedData(dueDate));
        }
        localStorage.setItem(STORAGE_KEYS.ONBOARDING_DATA, JSON.stringify(onboardingData));

        setOnboardingFinished(true);
        setOnboardingAnswers(sender.data);
        trackSurveyFinished?.(sender.data, {});

        // Call the completion callback
        onSurveyComplete?.(programDetermined, sender.data);
      } catch (err) {
        options.showDataSavingError();
        ErrorsService.message(
          `[SurveyHandlers] Survey completion error: ${
            err instanceof Error ? err.message : String(err)
          }`,
          {
            extra: { error: err },
          },
        );
        onError?.(err instanceof Error ? err.message : 'Unknown error');
      }
    },
    [setOnboardingFinished, setOnboardingAnswers, trackSurveyFinished, onSurveyComplete, onError],
  );

  const handleCurrentPageChanging = useCallback(
    (sender: SurveySender, options: SurveyOptions) => {
      options.newCurrentPage.propertyHash.navigationButtonsVisibility = 'both';
      sender.pagePrevText = '< Previous Question';
      sender.pageNextText = 'Next';

      if (options.oldCurrentPage.propertyHash.name === 'Menopause') {
        handleMenopauseSymptoms(options, menopauseSymptoms, setMenopauseSymptoms);
      }

      if (
        options.newCurrentPage.propertyHash.name === 'Menopause' &&
        options.oldCurrentPage.propertyHash.name !== 'DR Check'
      ) {
        const questions = getMenopauseSymptoms(options.newCurrentPage.questions);
        setMenopauseSymptoms({ ...menopauseSymptoms, ...questions });
      }

      const { currentPage } = sender;
      const questions = currentPage.elements;

      questions.forEach(question => {
        if (question.name === 'symptoms') {
          const symptoms = getMenopauseSymptoms(question.value);
          setMenopauseSymptoms(symptoms);
        }
      });
    },
    [menopauseSymptoms, setMenopauseSymptoms],
  );

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const handleCurrentPageChanged = useCallback((_sender: SurveySender, _options: SurveyOptions) => {
    // Onboarding survey doesn't is not handling current page changed, but might need to in the future
  }, []);

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const handleValueChanged = useCallback((_sender: SurveySender, _options: SurveyOptions) => {
    // Onboarding survey doesn't is not handling value changed, but might need to in the future
  }, []);

  return {
    handleSurveyComplete,
    handleCurrentPageChanging,
    handleCurrentPageChanged,
    handleValueChanged,
  };
};
